import { DraftIcpDto, IcpSegmentDto, IcpStateDto, IcpVersionDto } from "@growth/dto";
import { Badge, Button, cn, Field, Input, Textarea } from "@growth/ui";
import {
  Check,
  CircleAlert,
  CircleCheck,
  LoaderCircle,
  Minus,
  PenLine,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ErrorText, PageHeader, selectClass } from "../../components/page-header";
import { useActive } from "../../lib/active";
import { apiErrorMessage } from "../../lib/api-error";
import { formatDate } from "../../lib/format";
import {
  useConfirmIcp,
  useDiscardIcpDraft,
  useIcp,
  useRedoIcp,
  useSaveSegment,
  useStartIcpDraft,
} from "../../lib/icp";
import { useBrandReadiness } from "../../lib/leads-readiness";
import { card, Panel } from "./shared";

export const WEIGHT_LABEL: Record<number, string> = {
  3: "Must have",
  2: "Matters",
  1: "Nice to have",
};

const ROLE_LABEL = { budget_holder: "Budget holder", champion: "Champion", user: "User" } as const;

/**
 * The ideal customer profile (real data). The ICP builder drafts it from the confirmed brand
 * profile and claims plus the customers the team names; a person checks, edits or redoes it and
 * confirms it. Versions are kept. Accounts are judged against the version in use.
 */
export const IcpPage = () => {
  const { brand } = useActive();
  const icp = useIcp();
  const [starting, setStarting] = useState(false);
  const data = icp.data;

  return (
    <section className="max-w-6xl space-y-6">
      <PageHeader eyebrow={`${brand?.name ?? ""} · Leads`} title="Ideal customer profile">
        Who should buy, written as checks that need proof. The agents judge every account against
        the version in use, and they only read what a person has confirmed.
      </PageHeader>
      {icp.isError && <ErrorText>{apiErrorMessage(icp.error)}</ErrorText>}
      {icp.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {data && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_18rem]">
          <div className="min-w-0 space-y-4">
            {data.pending?.status === "drafting" ? (
              <>
                <Drafting pending={data.pending} />
                {data.inUse && <InUse version={data.inUse} />}
              </>
            ) : data.draft ? (
              <DraftReview draft={data.draft} inUse={data.inUse} />
            ) : data.pending?.status === "failed" ? (
              <>
                <Failed pending={data.pending} />
                <StartDraft basis={data.pending.basis} retry onDone={() => setStarting(false)} />
              </>
            ) : starting || !data.inUse ? (
              <StartDraft
                basis={data.inUse?.basis}
                onCancel={data.inUse ? () => setStarting(false) : undefined}
                onDone={() => setStarting(false)}
              />
            ) : (
              <InUse version={data.inUse} onNew={() => setStarting(true)} />
            )}
          </div>
          <aside className="order-first space-y-4 xl:order-none">
            <Readiness />
            {data.history.length > 0 && (
              <div className={cn(card, "space-y-2 p-4 text-sm")}>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">
                  Versions
                </h2>
                <ul className="space-y-1">
                  {data.history.map((row) => (
                    <li key={row.version} className="flex justify-between gap-2">
                      <span>
                        v{row.version}
                        {row.version === data.inUse?.version && (
                          <Badge tone="success" className="ml-2">
                            In use
                          </Badge>
                        )}
                      </span>
                      <span className="text-xs text-muted">
                        {formatDate(row.confirmedAt)} · {row.confirmedBy.split(" ")[0]}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs leading-relaxed text-muted">
              Fit is the share of check points an account passes. Unknown is not a fail: it means
              the agent found no proof either way. Timing signals change priority, never fit.
            </p>
          </aside>
        </div>
      )}
    </section>
  );
};

// ─── What the ICP is built from (real data) ──────────────────────────────────────────────────

const STATE_BADGE = {
  confirmed: { label: "Confirmed", className: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  check: { label: "Check this", className: "bg-amber-50 text-amber-800 ring-amber-200" },
  empty: { label: "Missing", className: "bg-red-50 text-red-700 ring-red-200" },
  redoing: { label: "Redoing", className: "bg-blue-50 text-accent ring-blue-200" },
} as const;

const Readiness = () => {
  const readiness = useBrandReadiness();
  return (
    <div className={cn(card, "space-y-3 p-4")}>
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">Built from</h2>
        <p className="mt-1 text-xs text-muted">
          Live from Brand setup and Claims. Drafting waits until all of these are confirmed.
        </p>
      </div>
      {readiness.pending ? (
        <LoaderCircle className="size-4 animate-spin text-muted" aria-label="Loading" />
      ) : (
        <ul className="space-y-2">
          {readiness.parts.map((part) => (
            <li key={part.key} className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm text-ink">{part.label}</p>
                <p className="text-[11px] text-muted">{part.why}</p>
              </div>
              <Badge className={STATE_BADGE[part.state].className}>
                {STATE_BADGE[part.state].label}
              </Badge>
            </li>
          ))}
        </ul>
      )}
      {!readiness.pending && !readiness.ready && (
        <p className="text-xs text-amber-800">
          {readiness.suggestedClaims > 0
            ? `${readiness.suggestedClaims} suggested claim${readiness.suggestedClaims === 1 ? " waits" : "s wait"} for review. `
            : ""}
          Finish these in{" "}
          <Link to="/setup" className="underline">
            Brand setup
          </Link>{" "}
          and{" "}
          <Link to="/claims" className="underline">
            Claims
          </Link>
          .
        </p>
      )}
    </div>
  );
};

// ─── Starting a draft ────────────────────────────────────────────────────────────────────────

type Row = { name: string; why: string };

const ExampleRows = ({
  label,
  hint,
  rows,
  onChange,
  placeholder,
}: {
  label: string;
  hint: string;
  rows: Row[];
  onChange: (rows: Row[]) => void;
  placeholder: Row;
}) => (
  <fieldset className="space-y-2">
    <legend className="text-xs font-medium text-ink">{label}</legend>
    <p className="text-xs text-muted">{hint}</p>
    {rows.map((row, index) => (
      <div key={index} className="grid gap-2 sm:grid-cols-[14rem_minmax(0,1fr)_auto]">
        <Input
          aria-label={`${label} ${index + 1}: name`}
          placeholder={placeholder.name}
          value={row.name}
          maxLength={120}
          onChange={(event) =>
            onChange(
              rows.map((item, i) => (i === index ? { ...item, name: event.target.value } : item)),
            )
          }
        />
        <Input
          aria-label={`${label} ${index + 1}: why`}
          placeholder={placeholder.why}
          value={row.why}
          maxLength={300}
          onChange={(event) =>
            onChange(
              rows.map((item, i) => (i === index ? { ...item, why: event.target.value } : item)),
            )
          }
        />
        <Button
          type="button"
          variant="ghost"
          aria-label="Remove"
          onClick={() => onChange(rows.filter((_, i) => i !== index))}
        >
          <X className="size-4" />
        </Button>
      </div>
    ))}
    {rows.length < 10 && (
      <Button
        type="button"
        variant="ghost"
        onClick={() => onChange([...rows, { name: "", why: "" }])}
      >
        <Plus className="size-4" /> Add one
      </Button>
    )}
  </fieldset>
);

const StartDraft = ({
  basis,
  retry = false,
  onCancel,
  onDone,
}: {
  basis?: IcpVersionDto["basis"];
  retry?: boolean;
  onCancel?: () => void;
  onDone: () => void;
}) => {
  const { atLeast } = useActive();
  const readiness = useBrandReadiness();
  const draft = useStartIcpDraft();
  const [best, setBest] = useState<Row[]>(basis?.bestCustomers ?? [{ name: "", why: "" }]);
  const [poor, setPoor] = useState<Row[]>(basis?.poorFits ?? []);
  const [note, setNote] = useState(basis?.note ?? "");
  const clean = (rows: Row[]) =>
    rows.map((row) => ({ name: row.name.trim(), why: row.why.trim() })).filter((row) => row.name);
  const input: DraftIcpDto = {
    bestCustomers: clean(best),
    poorFits: clean(poor),
    note: note.trim(),
  };
  const blocked = !readiness.ready;

  return (
    <Panel
      title={
        retry ? "Try again" : basis ? "Start a new version" : "Draft the ideal customer profile"
      }
      about="The ICP builder reads the confirmed brand profile and claims, plus what you tell it here. Real customers make it far better than guesses."
    >
      <ExampleRows
        label="Your best customers"
        hint="Who already buys, or the deals you'd most like to repeat. Name the organisation and why they bought."
        rows={best}
        onChange={setBest}
        placeholder={{
          name: "e.g. A business school careers team",
          why: "e.g. Coaches MBA students for interviews; buys per cohort",
        }}
      />
      <ExampleRows
        label="Poor fits"
        hint="Who looked right but wasn't. The builder turns these into exclusions."
        rows={poor}
        onChange={setPoor}
        placeholder={{
          name: "e.g. Colleges with one careers officer",
          why: "e.g. No budget, no time to roll it out",
        }}
      />
      <Field
        label="Anything else (optional)"
        hint="Markets, deal size, who runs outreach, what to avoid."
      >
        {(props) => (
          <Textarea
            {...props}
            rows={2}
            maxLength={2000}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        )}
      </Field>
      {blocked && !readiness.pending && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Confirm the brand profile and claims first (see “Built from”). The ICP is only as good as
          what it's built on.
        </p>
      )}
      {draft.isError && <ErrorText>{apiErrorMessage(draft.error)}</ErrorText>}
      {atLeast("EDITOR") && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            disabled={blocked || draft.isPending || input.bestCustomers.length === 0}
            onClick={() => draft.mutate(input, { onSuccess: onDone })}
          >
            {draft.isPending ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : (
              <Sparkles className="size-4" />
            )}
            {retry ? "Try again" : "Draft the ICP"}
          </Button>
          {onCancel && (
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <span className="text-xs text-muted">
            Takes a minute or two. Counts toward this brand's weekly research budget.
          </span>
        </div>
      )}
    </Panel>
  );
};

// ─── While the builder works, or after it failed ─────────────────────────────────────────────

type Pending = NonNullable<IcpStateDto["pending"]>;

const Drafting = ({ pending }: { pending: Pending }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const seconds = Math.max(0, Math.round((now - new Date(pending.startedAt).getTime()) / 1000));
  return (
    <section className={cn(card, "space-y-3 border-blue-200 p-5")}>
      <div className="flex items-center gap-3">
        <LoaderCircle className="size-5 animate-spin text-accent" />
        <h2 className="font-serif text-xl text-ink">
          {pending.redoNote ? `Redoing v${pending.version}` : `Drafting v${pending.version}`}
        </h2>
        <span className="ml-auto text-xs tabular-nums text-muted">
          {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
        </span>
      </div>
      <p className="text-sm text-muted">
        The ICP builder is reading the confirmed brand profile and claims, then the sites of the
        customers you named, to work out what they have in common. It usually takes a minute or two.
        You can leave this page; the draft waits here for you.
      </p>
      {pending.redoNote && (
        <p className="rounded-md bg-blue-50 px-3 py-2 text-xs text-accent">
          Your note: “{pending.redoNote}”
        </p>
      )}
      {pending.basis.bestCustomers.length > 0 && (
        <p className="text-xs text-muted">
          Best customers: {pending.basis.bestCustomers.map((row) => row.name).join("; ")}
        </p>
      )}
    </section>
  );
};

const Failed = ({ pending }: { pending: Pending }) => {
  const { atLeast } = useActive();
  const discard = useDiscardIcpDraft();
  return (
    <section className={cn(card, "space-y-3 border-red-200 p-5")}>
      <h2 className="flex items-center gap-2 font-serif text-xl text-ink">
        <CircleAlert className="size-5 text-red-600" /> Drafting v{pending.version} stopped
      </h2>
      <p className="text-sm text-ink">{pending.error ?? "The ICP builder stopped."}</p>
      <p className="text-xs text-muted">
        Nothing changed in the version in use. Try again below, or discard this attempt.
      </p>
      {atLeast("EDITOR") && (
        <Button variant="ghost" disabled={discard.isPending} onClick={() => discard.mutate()}>
          <X className="size-4" /> Discard this attempt
        </Button>
      )}
      {discard.isError && <ErrorText>{apiErrorMessage(discard.error)}</ErrorText>}
    </section>
  );
};

// ─── Reviewing a draft ───────────────────────────────────────────────────────────────────────

/** Plain-words list of what a draft changes compared with the version in use. */
const changesFrom = (draft: IcpVersionDto, inUse: IcpVersionDto | null): string[] => {
  if (!inUse) return [];
  const out: string[] = [];
  for (const segment of draft.segments) {
    const before = inUse.segments.find((item) => item.id === segment.id);
    if (!before) {
      out.push(`New segment: ${segment.name}`);
      continue;
    }
    for (const item of segment.checks) {
      const old = before.checks.find((candidate) => candidate.id === item.id);
      if (!old) out.push(`${segment.name}: new check “${item.text}”`);
      else if (old.text !== item.text || old.weight !== item.weight)
        out.push(`${segment.name}: changed check “${item.text}”`);
    }
    for (const old of before.checks)
      if (!segment.checks.some((item) => item.id === old.id))
        out.push(`${segment.name}: removed check “${old.text}”`);
  }
  for (const old of inUse.segments)
    if (!draft.segments.some((item) => item.id === old.id))
      out.push(`Removed segment: ${old.name}`);
  return out;
};

const DraftReview = ({ draft, inUse }: { draft: IcpVersionDto; inUse: IcpVersionDto | null }) => {
  const { atLeast } = useActive();
  const confirm = useConfirmIcp();
  const discard = useDiscardIcpDraft();
  const redo = useRedoIcp();
  const [redoing, setRedoing] = useState(false);
  const [note, setNote] = useState("");
  const changes = changesFrom(draft, inUse);
  const error = confirm.error ?? discard.error ?? redo.error;

  return (
    <>
      <section className={cn(card, "space-y-4 border-amber-300 p-5")}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="font-serif text-2xl text-ink">Draft v{draft.version}: check it</h2>
            <p className="text-xs text-muted">
              Nothing uses this until you confirm it.{" "}
              {inUse && `v${inUse.version} stays in use until then.`}
            </p>
          </div>
          <Badge className="bg-amber-50 text-amber-800 ring-amber-200">Check this</Badge>
        </div>
        {draft.redoNote && (
          <p className="rounded-md bg-blue-50 px-3 py-2 text-xs text-accent">
            Redone after your note: “{draft.redoNote}”
          </p>
        )}
        <p className="text-sm leading-relaxed text-ink">{draft.rationale}</p>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
          Built from:
          {draft.usedProfile.map((item) => (
            <Badge key={item}>{item}</Badge>
          ))}
        </div>
        <Sources version={draft} />
        {changes.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-ink">What changes from v{inUse?.version}</p>
            <ul className="space-y-0.5 text-xs text-muted">
              {changes.map((change) => (
                <li key={change} className="flex gap-1.5">
                  {change.includes("removed") || change.startsWith("Removed") ? (
                    <Minus className="mt-0.5 size-3 text-red-600" />
                  ) : (
                    <Plus className="mt-0.5 size-3 text-emerald-600" />
                  )}
                  {change}
                </li>
              ))}
            </ul>
          </div>
        )}
        {redoing && (
          <div className="flex flex-wrap gap-2">
            <Input
              aria-label="What should change"
              placeholder="e.g. Drop TAFEs for now; add private universities"
              className="min-w-64 flex-1"
              maxLength={1000}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
            <Button
              disabled={note.trim().length < 3 || redo.isPending}
              onClick={() =>
                redo.mutate(note.trim(), {
                  onSuccess: () => {
                    setRedoing(false);
                    setNote("");
                  },
                })
              }
            >
              Redo
            </Button>
            <Button variant="ghost" onClick={() => setRedoing(false)}>
              Cancel
            </Button>
          </div>
        )}
        {atLeast("EDITOR") && !redoing && (
          <div className="flex flex-wrap gap-2">
            <Button disabled={confirm.isPending} onClick={() => confirm.mutate(draft.version)}>
              <Check className="size-4" /> Looks right, use v{draft.version}
            </Button>
            <Button variant="secondary" onClick={() => setRedoing(true)}>
              <RotateCcw className="size-4" /> Redo with a note
            </Button>
            <Button variant="ghost" disabled={discard.isPending} onClick={() => discard.mutate()}>
              <X className="size-4" /> Discard the draft
            </Button>
          </div>
        )}
        {error && <ErrorText>{apiErrorMessage(error)}</ErrorText>}
      </section>
      <Tiers tiers={draft.tiers} />
      {draft.segments.map((segment) => (
        <SegmentCard key={segment.id} segment={segment} editable />
      ))}
    </>
  );
};

/** The pages the builder read or cited, the basis it was given, and what it cost. */
const Sources = ({ version }: { version: IcpVersionDto }) => (
  <details className="text-xs text-muted">
    <summary className="cursor-pointer text-accent">
      What it was given and read · US${version.costUsd.toFixed(2)}
    </summary>
    <div className="mt-2 space-y-2">
      {version.basis.bestCustomers.length > 0 && (
        <p>
          Best customers:{" "}
          {version.basis.bestCustomers
            .map((row) => (row.why ? `${row.name} (${row.why})` : row.name))
            .join("; ")}
        </p>
      )}
      {version.basis.poorFits.length > 0 && (
        <p>Poor fits: {version.basis.poorFits.map((row) => row.name).join("; ")}</p>
      )}
      {version.basis.note && <p>Note: {version.basis.note}</p>}
      {version.sources.length > 0 ? (
        <ul className="space-y-1">
          {version.sources.map((source) => (
            <li key={source.url} className="truncate">
              <a
                href={source.url}
                target="_blank"
                rel="noreferrer"
                className="text-accent underline"
              >
                {source.title || source.url}
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p>No web pages were needed.</p>
      )}
    </div>
  </details>
);

const InUse = ({ version, onNew }: { version: IcpVersionDto; onNew?: () => void }) => {
  const { atLeast } = useActive();
  return (
    <>
      <section className={cn(card, "space-y-3 p-5")}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-serif text-2xl text-ink">
              <CircleCheck className="size-5 text-emerald-600" /> v{version.version} in use
            </h2>
            <p className="text-xs text-muted">
              Confirmed {version.confirmedAt && formatDate(version.confirmedAt)} by{" "}
              {version.confirmedBy}. {version.segments.length} segments.
            </p>
          </div>
          {atLeast("EDITOR") && onNew && (
            <Button variant="secondary" onClick={onNew}>
              <PenLine className="size-4" /> Start a new version
            </Button>
          )}
        </div>
        <p className="text-sm leading-relaxed text-ink">{version.rationale}</p>
        <Sources version={version} />
      </section>
      <Tiers tiers={version.tiers} />
      {version.segments.map((segment) => (
        <SegmentCard key={segment.id} segment={segment} />
      ))}
    </>
  );
};

const Tiers = ({ tiers }: { tiers: IcpVersionDto["tiers"] }) => (
  <p className="text-xs text-muted">
    Fit tiers: <strong className="text-ink">A</strong> at {tiers.A}% of check points or more ·{" "}
    <strong className="text-ink">B</strong> {tiers.B}% · <strong className="text-ink">C</strong>{" "}
    {tiers.C}% · <strong className="text-ink">D</strong> below that.
  </p>
);

// ─── One segment ─────────────────────────────────────────────────────────────────────────────

const Part = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <h4 className="text-[10px] font-semibold uppercase tracking-widest text-muted">{title}</h4>
    {children}
  </div>
);

const SegmentCard = ({
  segment,
  editable = false,
}: {
  segment: IcpSegmentDto;
  editable?: boolean;
}) => {
  const { atLeast } = useActive();
  const [editing, setEditing] = useState(false);
  if (editing) return <SegmentEditor segment={segment} onClose={() => setEditing(false)} />;
  return (
    <article className={cn(card, "space-y-4 p-5")}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="max-w-2xl">
          <h3 className="font-serif text-xl text-ink">{segment.name}</h3>
          <p className="text-sm text-muted">{segment.summary}</p>
        </div>
        {editable && atLeast("EDITOR") && (
          <Button variant="ghost" onClick={() => setEditing(true)}>
            <PenLine className="size-4" /> Edit
          </Button>
        )}
      </div>
      <Part title="Filters (applied first, cheap)">
        <div className="flex flex-wrap gap-1.5">
          {segment.filters.map((filter) => (
            <Badge key={`${filter.label}-${filter.value}`} className="bg-paper text-ink">
              {filter.label}: {filter.value}
            </Badge>
          ))}
        </div>
      </Part>
      <Part title="Checks (each needs a quote and a link)">
        <ul className="divide-y divide-line rounded-lg border border-line">
          {segment.checks.map((item) => (
            <li key={item.id} className="grid gap-1 px-3 py-2 sm:grid-cols-[minmax(0,1fr)_7rem]">
              <div>
                <p className="text-sm text-ink">{item.text}</p>
                <p className="text-xs text-muted">Proof: {item.proof}</p>
              </div>
              <span
                className={cn(
                  "self-start text-xs font-medium sm:text-right",
                  item.weight === 3 ? "text-ink" : "text-muted",
                )}
              >
                {WEIGHT_LABEL[item.weight]} · {item.weight}
              </span>
            </li>
          ))}
        </ul>
      </Part>
      <div className="grid gap-4 sm:grid-cols-3">
        <Part title="Timing signals">
          <ul className="list-disc space-y-0.5 pl-4 text-sm text-ink">
            {segment.signals.map((signal) => (
              <li key={signal}>{signal}</li>
            ))}
            {!segment.signals.length && <li className="list-none text-muted">None</li>}
          </ul>
        </Part>
        <Part title="Buyers">
          <ul className="space-y-1 text-sm">
            {segment.buyers.map((buyer) => (
              <li key={buyer.role}>
                <span className="text-xs text-muted">{ROLE_LABEL[buyer.role]}:</span>{" "}
                <span className="text-ink">{buyer.titles.join(", ")}</span>
              </li>
            ))}
          </ul>
        </Part>
        <Part title="Never contact">
          <ul className="list-disc space-y-0.5 pl-4 text-sm text-ink">
            {segment.exclusions.map((exclusion) => (
              <li key={exclusion}>{exclusion}</li>
            ))}
            {!segment.exclusions.length && <li className="list-none text-muted">None</li>}
          </ul>
        </Part>
      </div>
    </article>
  );
};

const lines = (text: string) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const SegmentEditor = ({ segment, onClose }: { segment: IcpSegmentDto; onClose: () => void }) => {
  const save = useSaveSegment();
  const [name, setName] = useState(segment.name);
  const [summary, setSummary] = useState(segment.summary);
  const [checks, setChecks] = useState(segment.checks);
  const [filters, setFilters] = useState(
    segment.filters.map((filter) => `${filter.label}: ${filter.value}`).join("\n"),
  );
  const [signals, setSignals] = useState(segment.signals.join("\n"));
  const [exclusions, setExclusions] = useState(segment.exclusions.join("\n"));
  const [buyers, setBuyers] = useState(
    Object.fromEntries(
      (["budget_holder", "champion", "user"] as const).map((role) => [
        role,
        segment.buyers.find((buyer) => buyer.role === role)?.titles.join(", ") ?? "",
      ]),
    ) as Record<"budget_holder" | "champion" | "user", string>,
  );

  const submit = () => {
    const next: IcpSegmentDto = {
      ...segment,
      name: name.trim(),
      summary: summary.trim(),
      checks: checks.filter((item) => item.text.trim()),
      filters: lines(filters).map((line) => {
        const [label, ...rest] = line.split(":");
        return { label: (label ?? "").trim(), value: rest.join(":").trim() || "yes" };
      }),
      signals: lines(signals),
      exclusions: lines(exclusions),
      buyers: (["budget_holder", "champion", "user"] as const)
        .map((role) => ({
          role,
          titles: buyers[role]
            .split(",")
            .map((title) => title.trim())
            .filter(Boolean),
        }))
        .filter((buyer) => buyer.titles.length),
    };
    save.mutate(next, { onSuccess: onClose });
  };

  return (
    <article className={cn(card, "space-y-4 border-accent p-5")}>
      <div className="grid gap-3 sm:grid-cols-[16rem_minmax(0,1fr)]">
        <Field label="Segment">
          {(props) => (
            <Input
              {...props}
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Who they are, in one line">
          {(props) => (
            <Input
              {...props}
              value={summary}
              maxLength={400}
              onChange={(e) => setSummary(e.target.value)}
            />
          )}
        </Field>
      </div>
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-ink">Checks</legend>
        {checks.map((item, index) => (
          <div
            key={item.id}
            className="grid gap-2 rounded-lg bg-paper/60 p-2 sm:grid-cols-[minmax(0,1fr)_9rem_auto]"
          >
            <div className="space-y-2">
              <Input
                aria-label={`Check ${index + 1}`}
                value={item.text}
                maxLength={200}
                onChange={(event) =>
                  setChecks(
                    checks.map((row, i) =>
                      i === index ? { ...row, text: event.target.value } : row,
                    ),
                  )
                }
              />
              <Input
                aria-label={`Check ${index + 1}: what counts as proof`}
                placeholder="What counts as proof"
                value={item.proof}
                maxLength={300}
                onChange={(event) =>
                  setChecks(
                    checks.map((row, i) =>
                      i === index ? { ...row, proof: event.target.value } : row,
                    ),
                  )
                }
              />
            </div>
            <select
              aria-label={`Check ${index + 1}: weight`}
              className={selectClass}
              value={item.weight}
              onChange={(event) =>
                setChecks(
                  checks.map((row, i) =>
                    i === index ? { ...row, weight: Number(event.target.value) } : row,
                  ),
                )
              }
            >
              {[3, 2, 1].map((weight) => (
                <option key={weight} value={weight}>
                  {WEIGHT_LABEL[weight]}
                </option>
              ))}
            </select>
            <Button
              variant="ghost"
              aria-label={`Remove check ${index + 1}`}
              onClick={() => setChecks(checks.filter((_, i) => i !== index))}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
        <Button
          variant="ghost"
          onClick={() =>
            setChecks([...checks, { id: `c-${Date.now()}`, text: "", weight: 2, proof: "" }])
          }
        >
          <Plus className="size-4" /> Add a check
        </Button>
      </fieldset>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Filters" hint="One per line, as “Label: value”">
          {(props) => (
            <Textarea
              {...props}
              rows={4}
              value={filters}
              onChange={(e) => setFilters(e.target.value)}
            />
          )}
        </Field>
        <Field label="Timing signals" hint="One per line">
          {(props) => (
            <Textarea
              {...props}
              rows={4}
              value={signals}
              onChange={(e) => setSignals(e.target.value)}
            />
          )}
        </Field>
        <Field label="Never contact" hint="One per line">
          {(props) => (
            <Textarea
              {...props}
              rows={4}
              value={exclusions}
              onChange={(e) => setExclusions(e.target.value)}
            />
          )}
        </Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {(["budget_holder", "champion", "user"] as const).map((role) => (
          <Field key={role} label={`${ROLE_LABEL[role]} titles`} hint="Comma-separated">
            {(props) => (
              <Input
                {...props}
                value={buyers[role]}
                onChange={(event) => setBuyers({ ...buyers, [role]: event.target.value })}
              />
            )}
          </Field>
        ))}
      </div>
      {save.isError && <ErrorText>{apiErrorMessage(save.error)}</ErrorText>}
      <div className="flex gap-2">
        <Button
          disabled={save.isPending || !name.trim() || !checks.some((item) => item.text.trim())}
          onClick={submit}
        >
          Save to the draft
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </article>
  );
};
